<template>
  <div
    class="app-container"
    v-loading.body="listLoading"
  >
    <list-filter
      :base="base"
      :idField="idField"
      :searchText="searchText"
      :filterCols="$attrs.filterCols"
      :optionCols="$attrs.optionCols"
      :updListQuery="updListQuery"
      :refreshList="refreshList"
      :srchFields="srchFields"
      :showExSearch="showExSearch"
      :ordersIDName="$attrs.ordersIDName"
      :dateRangeName="$attrs.dateRangeName"
      :allowDateNames="$attrs.allowDateNames"
      :hasCreate="hasCreate"
      :enableAdvSrch="enableAdvSrch"
      :isBrandField="isBrandField"
      :fetchFields="fetchFields"
      :getFilters="getFilters"
      :clickCreate="clickCreate"
    >
      <template slot-scope="srch" slot="exsrch">
        <slot name="exsrch" srch></slot>
      </template>
      <template slot-scope="act" slot="acts">
        <slot name="acts" act></slot>
      </template>
    </list-filter>
    <div
      v-if="isShowSelection"
      class="multi-act-container"
    >
      <el-tooltip
        class="item"
        effect="light"
        placement="right"
        :openDelay="500"
        :content="$t('api.multiActionTip')"
      >
        <div>
          <slot name="macts" mact></slot>
        </div>
      </el-tooltip>
    </div>
    <viewer
      :images="coverImgs"
      :options="{}"
      class="list-viewer"
      ref="viewer"
      @inited="initedViewer"
    >
      <img
        v-for="img in coverImgs"
        :src="img.src"
        :key="img.id"
      />
    </viewer>
    <el-table
      stripe border fit
      v-bind="$attrs"
      style="width: 100%"
      :data="list"
      :row-class-name="tableRowClassName"
      @filter-change="filterChange"
      @selection-change="selectionChange"
      @select="selectClick"
      ref="table"
    >
      <el-table-column
        v-if="isShowSelection"
        type="selection"
        align="center"
        width="55"
      >
      </el-table-column>
      <list-index
        :listQuery="listQuery"
        :idField="idField"
        :unhideIDField="unhideIDField"
      >
      </list-index>
      <cover-img
        v-if="isShowCoverImg"
        :isForm="false"
        :name="coverImgKey"
        :idField="idField"
        :nameField="nameField"
        :showViewer="showViewer"
      >
      </cover-img>
      <slot></slot>
      <act-col
        :name="base"
        :platform="platform"
        :idText="idText"
        :idField="idField"
        :nameField="nameField"
        :titleField="titleField"
        :type="statusType"
        :getList="getList"
        :updateStatus="updateStatus"
        :deleteOne="deleteOne"
        :isReadOnly="isReadOnly"
        :isShowDelBtn="$attrs.isShowDelBtn"
        :showDelBtnFunc="$attrs.showDelBtnFunc"
        :delActFunc="$attrs.delActFunc"
        :isNoDelBtn="$attrs.isNoDelBtn"
        :width="$attrs.actWidth"
        :minWidth="$attrs.actMinWidth"
        ref="actions"
      >
        <template slot-scope="relBtn">
          <slot name="btns" btn
            :rowid="relBtn.rowid"
            :rowname="relBtn.rowname"
            :row="relBtn.row"
          >
          </slot>
        </template>
      </act-col>
    </el-table>
    <pager
      :getList="getList"
      :listQuery="listQuery"
      :total="total"
      ref="pager"
    >
    </pager>
  </div>
</template>

<script>

import { getImgURL } from '@/utils/path'
import { updateRoute } from '@/utils/route'
import { getOptMap } from '@/utils/i18n'

const defListQuery = {
  page: 1,
  limit: 10
}

const filterMapping = {
  bid: 'branch_name',
  region_id: 'region_name',
  sale_region_id: 'sale_region_name'
}

export default {
  name: 'list-table',
  props: {
    base: {
      type: String,
      required: true
    },
    searchText: {
      type: String
    },
    showExSearch: {
      type: Object
    },
    platform: {
      type: String
    },
    isShowSelection: {
      type: Boolean,
      default: false
    },
    isShowCoverImg: {
      type: Boolean,
      default: false
    },
    coverImgKey: {
      type: String,
      default: 'cover_image'
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    hasCreate: {
      type: Boolean,
      default: true
    },
    enableAdvSrch: {
      type: Boolean,
      default: false
    },
    isBrandField: {
      type: Boolean,
      default: false
    },
    idField: {
      type: String,
      default: 'id'
    },
    unhideIDField: {
      type: String,
      default: 'unhide_id'
    },
    idText: {
      type: String
    },
    nameField: {
      type: String
    },
    titleField: {
      type: String
    },
    statusType: {
      type: String
    },
    sort: {
      type: String
    },
    fetchList: {
      type: Function,
      required: true
    },
    fetchFields: {
      type: Function
    },
    srchFields: {
      type: Array,
      default: () => ['name']
    },
    statusName: {
      type: String,
      default: 'status'
    },
    checkStatus: {
      type: Function,
      default: (status) => {
        return status === 1
      }
    },
    checkDeleted: {
      type: Function,
      default: () => {
        return false
      }
    },
    clickCreate: {
      type: Function,
      required: false
    },
    updateStatus: {
      type: Function,
      required: false
    },
    deleteOne: {
      type: Function,
      required: false
    }
  },
  data () {
    return {
      list: null,
      total: 0,
      listLoading: true,
      filters: {},
      multiValues: [],
      shiftClick: false,
      lastChecked: null,
      coverImgs: [],
      dateRange: null,
      listQuery: Object.assign({}, defListQuery)
    }
  },
  created () {
  },
  mounted () {
    addEventListener('keydown', this.keyDown, false)
    addEventListener('keyup', this.keyUp, false)
    this.$ebus.$on('renewList', e => {
      this.getList()
    })
  },
  beforeDestroy () {
    removeEventListener('keydown', this.keyDown)
    removeEventListener('keyup', this.keyUp)
    this.$ebus.$off('renewList')
  },
  watch: {
    list (newList) {
      this.coverImgs.splice(0, this.coverImgs.length)
      for (var i in newList) {
        if (newList[i] && newList[i][this.coverImgKey]) {
          this.coverImgs.push(
            {
              id: newList[i][this.idField] + '.' + i,
              src: this.getImgURL(newList[i][this.coverImgKey])
            }
          )
        }
      }
    }
  },
  methods: {
    getImgURL,
    getOptMap,
    updateRoute,
    initedViewer (viewer) {
      this.$viewer = viewer
    },
    showViewer (id, idx) {
      for (var i in this.coverImgs) {
        if (this.coverImgs[i].id === id + '.' + idx) {
          this.$viewer.view(i)
          break
        }
      }
    },
    getList () {
      if (this.sort) {
        this.listQuery.sort = this.sort
      }
      this.listQuery.page = parseInt(this.$route.query.page) || defListQuery.page
      this.listQuery.limit = parseInt(this.$route.query.limit) || defListQuery.limit
      this.listLoading = true
      this.fetchList(this.listQuery, this.platform).then(response => {
        this.list = response.data
        this.total = response.total_count
        this.$nextTick(() => { // prevent refs undefined error
          // update internal page to sync with current page
          if (this.$refs.pager) {
            this.$refs.pager.update(this.listQuery.page)
          }
        })
      }).catch(error => {
        // error
        console.log(error)
      }).then(() => {
        // always executed
        this.listLoading = false
      })
    },
    removeEmpty (obj) {
      Object.keys(obj).forEach((key) => {
        if (obj[key] == null) {
          delete obj[key]
        }
      })
      return obj
    },
    refreshList () {
      this.getList()
    },
    getFilters () {
      let filters = []
      Object.keys(this.filters).forEach((key) => {
        switch (key) {
          case 'query':
            filters.push({
              field: 'search',
              value: this.$route.query.query
            })
            break
          case 'advsrch':
            // Filter out advanced search filter
            break
          case 'date_range':
            if (this.dateRange) {
              filters.push(this.dateRange)
            }
            break
          case 'default':
            let f = JSON.parse(this.filters[key])
            Object.keys(f).forEach((field) => {
              let v = f[field]
              if (typeof filterMapping[field] !== 'undefined') {
                v += ' (' + this.$route.query[filterMapping[field]] + ')'
              }
              filters.push({
                field: field,
                value: v
              })
            })
            break
          default:
            if (this.$route.query[key]) {
              let m = this.getOptMap(this.$t, key)
              let arr = this.$route.query[key].split(',')
              let str = []
              for (let i = 0; i < arr.length; i++) {
                let v = m.filter((e) => {
                  return (e.value + '') === arr[i]
                })
                if (v.length > 0) {
                  str.push(v[0].label)
                }
              }
              filters.push({
                field: key,
                value: str.join(', ')
              })
            }
            break
        }
      })
      return filters
    },
    isObject (value) {
      return value && typeof value === 'object' && value.constructor === Object
    },
    updListQuery (key, filter, dateRange) {
      if (filter !== null && filter !== '') {
        if (this.isObject(filter)) {
          this.filters[key] = JSON.stringify(this.removeEmpty(filter))
        } else {
          this.filters[key] = filter
        }
      } else {
        delete this.filters[key]
        if (this.filters.default) {
          let defFilter = JSON.parse(this.filters.default)
          delete defFilter[key]
          this.filters.default = JSON.stringify(defFilter)
        }
      }
      let arr = []
      for (let s in this.filters) {
        if (this.filters[s] !== '' && this.filters[s] !== '{}') {
          arr.push(this.filters[s])
        }
      }
      if (key === 'date_range') {
        if (filter !== '' &&
          typeof dateRange !== 'undefined'
        ) {
          this.dateRange = dateRange
        } else {
          this.dateRange = null
        }
      }
      switch (arr.length) {
        case 0:
          delete this.listQuery.filter
          break
        case 1:
          this.listQuery.filter = arr[0]
          break
        default:
          this.listQuery.filter = '{"$and":[' + arr.join() + ']}'
          break
      }
    },
    filterChange (rules) {
      var field = ''
      var values = []
      for (var f in rules) {
        field = f
        for (var v in rules[f]) {
          values.push(rules[f][v])
        }
      }
      this.updateRoute(field, values.join(','))
    },
    selectionChange (v) {
      this.multiValues = v
    },
    isChecked (v, row) {
      for (let idx in v) {
        if (v[idx].id === row.id) {
          return true
        }
      }
      return false
    },
    findIdx (id) {
      for (let idx in this.list) {
        if (this.list[idx].id === id) {
          return idx
        }
      }
      return -1
    },
    selectClick (v, row) {
      if (!this.lastChecked) {
        this.lastChecked = row.id
        return
      }
      if (this.shiftClick) {
        let st = this.isChecked(v, row)
        let start = this.findIdx(row.id)
        let end = this.findIdx(this.lastChecked)
        let l = this.list.slice(Math.min(start, end), Math.max(start, end) + 1)
        for (let idx in l) {
          // NOTE: nextclick() to fix selected value not updated issue
          this.$nextTick(() => {
            this.$refs.table.toggleRowSelection(l[idx], st)
          })
        }
      }
      this.lastChecked = row.id
    },
    tableRowClassName ({row, rowIndex}) {
      if (this.checkStatus(row[this.statusName])) {
        return 'disabled-row'
      }
      if (this.checkDeleted(row)) {
        return 'deleted-row'
      }
      return ''
    },
    keyDown (event) {
      let key = event.keyCode
      if (key === 16) {
        this.shiftClick = true
      }
    },
    keyUp (event) {
      let key = event.keyCode
      if (key === 16) {
        this.shiftClick = false
      }
    }
  }
}
</script>

<style rel="stylesheet/scss" lang="scss">
  .list-viewer {
    display: none;
  }
</style>
